import type { FC, ReactNode } from "react";
import { useState } from "react";

import { CopyCodeButton } from "@ui/components/CopyCodeButton";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { Button } from "@jti/core-components/components/Button";
import { SupportingText } from "@jti/core-components/components/SupportingText";

import { ReferralPersonalCodeBlocks } from "../../ReferralPersonalCodeBlocks";
import { ReferralPersonalCodeIcon, type ReferralPersonalCodeIconPropsType } from "../../ReferralPersonalCodeIcon";

import styles from "./ReferralPersonalCodeWithBackground.module.scss";

const { sanitize } = dompurify;

export type ReferralPersonalCodeWithBackgroundProps = {
  items: ReferralPersonalCodeIconPropsType[];
  backgroundImage?: string;
  backgroundImageTablet?: string;
  backgroundImageMobile?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  infoTitle?: string;
  code: string;
  theme?: "light" | "dark";
  withBottomCta?: boolean;
  bottomCtaLabel?: string;
  bottomCtaLink?: string;
  handleBottomCtaClick?: () => void;
  ctaRow?: ReactNode;
  shareCodeVariant?: boolean;
  onShareClick?: () => void;
  shareButtonLabel?: string;
  shareTitle?: string;
};

export const ReferralPersonalCodeWithBackground: FC<ReferralPersonalCodeWithBackgroundProps> = ({
  items,
  backgroundImage,
  backgroundImageTablet,
  backgroundImageMobile,
  title,
  subtitle,
  description,
  code,
  theme = "dark",
  infoTitle,
  withBottomCta = false,
  bottomCtaLabel,
  bottomCtaLink,
  handleBottomCtaClick,
  ctaRow,
  shareCodeVariant,
  onShareClick,
  shareButtonLabel,
  shareTitle,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { __ } = useTranslations();

  const IMAGE_STYLES = {
    "--referral-code-background-image-desktop": `url(${backgroundImage})`,
    "--referral-code-background-image-tablet": `url(${backgroundImageTablet || backgroundImage})`,
    "--referral-code-background-image-mobile": `url(${
      backgroundImageMobile || backgroundImageTablet || backgroundImage
    })`,
  };

  return (
    <div
      className={classNames(styles.container, {
        ["dark-theme"]: theme === "dark",
        ["light-theme"]: theme === "light",
      })}
      style={IMAGE_STYLES as React.CSSProperties}
      data-testid="referral-personal-code"
    >
      <div className={styles.content}>
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subtitle}>{subtitle}</h3>
        <p
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: sanitize(description ?? ""),
          }}
        />
        {shareCodeVariant ? (
          <ReferralPersonalCodeBlocks
            code={code}
            shareButtonLabel={shareButtonLabel}
            onShareClick={onShareClick}
            items={items}
            infoTitle={infoTitle}
            shareTitle={shareTitle ?? ""}
            shareCodeVariant="shareButton"
          />
        ) : (
          <>
            <div className={styles.copyContainer}>
              <CopyCodeButton value={code} onCopy={() => setIsCopied(true)} />
            </div>
            {isCopied && (
              <div className={styles.copiedText} data-testid="copied-text">
                <UiCheckCircleIcon className={styles.copyIcon} />
                {__("copy.code.button")}
              </div>
            )}
            {infoTitle && <SupportingText data-testid={"your-personal-code-info"}>{infoTitle}</SupportingText>}
            <div className={styles.socialRow} data-testid={"social-media-block"}>
              {items.map((element: ReferralPersonalCodeIconPropsType) => (
                <ReferralPersonalCodeIcon
                  key={element.url}
                  img={element.img}
                  url={element.url}
                  trackingName={element.trackingName}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className={styles.bottomInfoSection}>
        {ctaRow}
        {withBottomCta && (
          <Button
            variant="text"
            type="cta"
            size="lg"
            href={bottomCtaLink ?? ""}
            onClick={handleBottomCtaClick}
            data-testid="bottomCta"
          >
            {bottomCtaLabel}
          </Button>
        )}
      </div>
    </div>
  );
};
